/**
 * Request Acknowledgement Modal (F-009)
 * Landlord requests tenant to acknowledge compliance documents
 */
import React, { useState, useEffect, useRef } from 'react';
import { complianceService } from '../../../services/api';

interface TenantLike {
    id?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    status?: string;
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    propertyId: string;
    propertyAddress: string;
    onSuccess: () => void;
    tenants?: TenantLike[];
}

const DOCUMENT_TYPES = [
    { key: 'GAS_SAFETY',         label: 'Gas Safety Certificate' },
    { key: 'EPC',                label: 'Energy Performance Certificate' },
    { key: 'HOW_TO_RENT',        label: 'How to Rent Guide' },
    { key: 'EICR',               label: 'Electrical Installation Report' },
    { key: 'DEPOSIT_PROTECTION', label: 'Deposit Protection' },
    { key: 'BUILDING_INSURANCE', label: 'Building Insurance' }
];

const fullName = (t: TenantLike) =>
    t.name?.trim() || `${t.firstName || ''} ${t.lastName || ''}`.trim();

const RequestAcknowledgementModal: React.FC<Props> = ({
    isOpen,
    onClose,
    propertyId,
    propertyAddress,
    onSuccess,
    tenants = []
}) => {
    // Pre-populate with the first active tenant; fall back to any tenant.
    // Editable — landlord can override before sending.
    const active = tenants.filter(t => (t.status || '').toLowerCase() !== 'former' && (t.status || '').toLowerCase() !== 'inactive');
    const primary = active[0] || tenants[0];
    const [tenantEmail, setTenantEmail] = useState(primary?.email || '');
    const [tenantName, setTenantName] = useState(primary ? fullName(primary) : '');

    // Re-sync whenever the modal opens with a fresh tenants list (e.g.
    // user opens the modal after the property's tenants have loaded).
    useEffect(() => {
        if (!isOpen) return;
        const t = active[0] || tenants[0];
        if (t) {
            setTenantEmail(prev => prev || t.email || '');
            setTenantName(prev => prev || fullName(t));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, tenants.length]);
    const [selectedDocs, setSelectedDocs] = useState<string[]>(['GAS_SAFETY', 'EPC', 'HOW_TO_RENT', 'BUILDING_INSURANCE']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleDocToggle = (key: string) => {
        setSelectedDocs(prev =>
            prev.includes(key)
                ? prev.filter(d => d !== key)
                : [...prev, key]
        );
    };

    // Hard guard against repeat submits. Setting `loading=true` happens
    // asynchronously, so back-to-back submits (rapid clicks, Enter twice,
    // or any global event re-dispatch) could fire the request multiple
    // times before the button re-renders disabled. Symptom in prod was
    // the acknowledgement email arriving 3 times. The ref blocks subsequent
    // entries until the promise resolves.
    const submittingRef = useRef(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (submittingRef.current) return;
        setError(null);

        if (!tenantEmail || selectedDocs.length === 0) {
            setError('Please enter tenant email and select at least one document');
            return;
        }

        submittingRef.current = true;
        setLoading(true);
        try {
            const response = await complianceService.requestAcknowledgement({
                propertyId,
                tenantEmail,
                tenantName: tenantName || 'Tenant',
                documents: selectedDocs
            });

            if (response.data?.emailSent === false) {
                setError(
                    response.data?.message
                        ? `Request created but email delivery failed: ${response.data.message}`
                        : 'Request created but email delivery failed. You can share the portal link manually.'
                );
                return;
            }

            alert('Acknowledgement request sent successfully!');
            onSuccess();
            onClose();
            setTenantEmail('');
            setTenantName('');
        } catch (err: any) {
            console.error('[RequestAck] Error:', err);
            const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to send request';
            setError(msg);
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                {/* Backdrop */}
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

                {/* Modal */}
                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                    <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                            <i className="fas fa-envelope text-green-600"></i>
                        </div>
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                Request Acknowledgement
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">{propertyAddress}</p>

                            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                                {error && (
                                    <div className="bg-red-50 border-l-4 border-red-400 p-3 text-sm text-red-700">
                                        {error}
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Tenant Email *</label>
                                    <input
                                        type="email"
                                        value={tenantEmail}
                                        onChange={(e) => setTenantEmail(e.target.value)}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
                                        placeholder="tenant@example.com"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Tenant Name</label>
                                    <input
                                        type="text"
                                        value={tenantName}
                                        onChange={(e) => setTenantName(e.target.value)}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
                                        placeholder="John Smith"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Documents to Acknowledge *</label>
                                    <div className="space-y-2">
                                        {DOCUMENT_TYPES.map(doc => (
                                            <label key={doc.key} className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedDocs.includes(doc.key)}
                                                    onChange={() => handleDocToggle(doc.key)}
                                                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                                />
                                                <span className="ml-2 text-sm text-gray-700">{doc.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                                    >
                                        {loading ? 'Sending...' : 'Send Request'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RequestAcknowledgementModal;
